import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';

import { USER_MANAGEMENT_API_CONFIG, UserManagementConfig } from '../config';
import { ContactPayload, ContactResponse, ContactError } from '../models/contact.model';

/**
 * Service for submitting contact forms via the User Management API.
 *
 * Covers two endpoints:
 *
 * | Method | Endpoint | Auth |
 * |--------|----------|------|
 * | `submitContact()` | `POST /api/contact` | None |
 * | `submitOrgContact()` | `POST /api/contact/org/:orgId` | Firebase Bearer token |
 *
 * @remarks
 * **Authentication:** The org endpoint requires a valid Firebase ID token.
 * The token is attached automatically by the application-level `authInterceptor` —
 * no additional setup is needed in consuming components.
 *
 * **Rate limiting:** Both endpoints enforce **5 requests per hour per IP**.
 * On HTTP 429 the service surfaces a `retryAfterSeconds` value derived from
 * the `Retry-After` response header so the UI can show a countdown.
 *
 * **Timeout:** Requests are cancelled after `config.timeout` milliseconds
 * (default 30 000 ms from `UserManagementConfig`).
 *
 * @example
 * ```typescript
 * // Public contact form (no auth required)
 * private readonly contactService = inject(ContactService);
 *
 * this.contactService.submitContact({
 *   name: 'Jane Doe',
 *   email: 'jane@example.com',
 *   subject: 'Hello',
 *   message: 'This is a test message with enough characters.'
 * }).subscribe({
 *   next: () => console.log('Sent!'),
 *   error: (err: ContactError) => console.error(err.message)
 * });
 * ```
 *
 * @example
 * ```typescript
 * // Organisation-scoped contact form (authenticated)
 * this.contactService.submitOrgContact(orgId, payload).subscribe({
 *   next: () => console.log('Org admins notified.'),
 *   error: (err: ContactError) => {
 *     if (err.status === 429 && err.retryAfterSeconds) {
 *       console.warn(`Rate limited. Retry in ${err.retryAfterSeconds}s`);
 *     }
 *   }
 * });
 * ```
 */
@Injectable({ providedIn: 'root' })
export class ContactService {
  private readonly _http = inject(HttpClient);
  private readonly _config: UserManagementConfig = inject(USER_MANAGEMENT_API_CONFIG);

  private readonly _baseUrl: string;

  /** Effective request timeout in milliseconds. Falls back to 30 000 ms. */
  private get _timeoutMs(): number {
    return this._config.timeout ?? 30_000;
  }

  constructor() {
    this._baseUrl = `${this._config.apiBaseUrl}/api/contact`;
    this._logDebug('ContactService initialized', { baseUrl: this._baseUrl });
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Public API
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Submit the public contact form.
   *
   * No authentication is required. The message is delivered to the address
   * configured in the server's `CONTACT_RECIPIENT_EMAIL` environment variable.
   *
   * @param payload - Form fields to send.
   * @returns Observable that emits {@link ContactResponse} on success.
   *
   * @throws {ContactError} `status 0`   — network error / no connection
   * @throws {ContactError} `status 400` — validation failure (`fieldErrors` populated)
   * @throws {ContactError} `status 429` — rate limit hit (`retryAfterSeconds` may be populated)
   * @throws {ContactError} `status 5xx` — server-side error
   *
   * @example
   * ```typescript
   * this.contactService.submitContact(this.form.getRawValue()).subscribe({
   *   next: () => this.state.set('success'),
   *   error: (err: ContactError) => {
   *     this.errorMessage.set(err.message);
   *     this.state.set('error');
   *   }
   * });
   * ```
   */
  submitContact(payload: ContactPayload): Observable<ContactResponse> {
    this._logDebug('submitContact', { payload });

    return this._http.post<ContactResponse>(this._baseUrl, payload).pipe(
      timeout(this._timeoutMs),
      map(response => {
        this._logDebug('submitContact succeeded');
        return response;
      }),
      catchError((err: HttpErrorResponse) => this._handleError('submitContact', err))
    );
  }

  /**
   * Submit a contact form targeted at a specific organisation's Admin members.
   *
   * Requires the caller to be authenticated (Firebase Bearer token) **and** to
   * be a member of the specified organisation.
   *
   * @param orgId   - MongoDB ObjectId of the target organisation.
   * @param payload - Form fields to send.
   * @returns Observable that emits {@link ContactResponse} on success.
   *
   * @throws {ContactError} `status 0`   — network error / no connection
   * @throws {ContactError} `status 400` — validation failure (`fieldErrors` populated)
   * @throws {ContactError} `status 401` — unauthenticated
   * @throws {ContactError} `status 403` — authenticated but not a member of the org
   * @throws {ContactError} `status 404` — org not found or has no Admin members
   * @throws {ContactError} `status 429` — rate limit hit (`retryAfterSeconds` may be populated)
   * @throws {ContactError} `status 5xx` — server-side error
   *
   * @remarks
   * The `Authorization: Bearer <token>` header is automatically attached by
   * the application-level `authInterceptor` for requests matching the base API URL —
   * no manual token management is needed in components.
   *
   * @example
   * ```typescript
   * // orgId typically comes from route params or an active-org signal
   * this.contactService.submitOrgContact(this.orgId(), this.form.getRawValue()).subscribe({
   *   next: () => this.state.set('success'),
   *   error: (err: ContactError) => {
   *     if (err.status === 429) {
   *       this.retryAfter.set(err.retryAfterSeconds ?? null);
   *     }
   *     this.errorMessage.set(err.message);
   *     this.state.set('error');
   *   }
   * });
   * ```
   */
  submitOrgContact(orgId: string, payload: ContactPayload): Observable<ContactResponse> {
    this._logDebug('submitOrgContact', { orgId, payload });

    const url = `${this._baseUrl}/org/${orgId}`;

    return this._http.post<ContactResponse>(url, payload).pipe(
      timeout(this._timeoutMs),
      map(response => {
        this._logDebug('submitOrgContact succeeded', { orgId });
        return response;
      }),
      catchError((err: HttpErrorResponse) => this._handleError('submitOrgContact', err))
    );
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Private helpers
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Centralised error handler — converts raw `HttpErrorResponse` objects into
   * typed {@link ContactError} objects with human-readable messages.
   *
   * @param operation - Service method name (used for debugging).
   * @param error     - Raw HTTP error from the Angular HTTP layer.
   */
  private _handleError(operation: string, error: HttpErrorResponse): Observable<never> {
    let message: string;
    let fieldErrors: string[] | undefined;
    let retryAfterSeconds: number | undefined;

    if (error.status === 0 || error.error instanceof ErrorEvent) {
      // Network error or client-side error (e.g. timeout from RxJS)
      message = 'Unable to reach the server. Please check your connection.';
    } else if (error.status === 400) {
      // express-validator returns an `errors` array
      const validationErrors: string[] | undefined = error.error?.errors?.map(
        (e: { msg: string }) => e.msg
      );
      fieldErrors = validationErrors?.length ? validationErrors : undefined;
      message = fieldErrors
        ? fieldErrors.join(' • ')
        : (error.error?.message ?? 'Invalid input. Please review the form and try again.');
    } else if (error.status === 401) {
      message = 'You must be signed in to send a message to this organisation.';
    } else if (error.status === 403) {
      message = 'You do not have permission to contact this organisation.';
    } else if (error.status === 404) {
      message = 'Organisation not found or has no admin contacts configured.';
    } else if (error.status === 429) {
      const retryAfterHeader = error.headers?.get('Retry-After');
      retryAfterSeconds = retryAfterHeader ? parseInt(retryAfterHeader, 10) : undefined;
      message = retryAfterSeconds
        ? `Too many submissions. Please wait ${retryAfterSeconds} seconds before trying again.`
        : 'Too many submissions. Please try again later.';
    } else if (error.status >= 500) {
      message = 'Something went wrong on our end. Please try again later.';
    } else {
      message = error.error?.message ?? 'An unexpected error occurred. Please try again.';
    }

    const contactError: ContactError = {
      operation,
      message,
      status: error.status,
      ...(fieldErrors ? { fieldErrors } : {}),
      ...(retryAfterSeconds !== undefined ? { retryAfterSeconds } : {}),
      originalError: error
    };

    this._logDebug(`${operation} failed`, { status: error.status, message });

    return throwError(() => contactError);
  }

  /**
   * Conditional debug logger — only emits when `config.debug` is `true`.
   *
   * @param message - Log message.
   * @param data    - Optional structured data to attach.
   */
  private _logDebug(message: string, data?: unknown): void {
    if (this._config.debug) {
      console.log(`[ContactService] ${message}`, data ?? '');
    }
  }
}
